<template>
  <div class="viewport viewport-with-top-nav viewport-compact">
    <TopNav></TopNav>
    <div v-if="loading" class="spinner-fill-page"><spinner></spinner></div>
    <template v-else>
      <div class="display-1 py-3">{{ $t('SignContractPage.title') }}</div>
      <p class="text-muted mb-4">{{ $t('SignContractPage.description') }}</p>
      <validation-observer ref="form">
        <div class="card card-shadow card-rounded border-0 mb-4">
          <div v-if="errors.length > 0" class="alert alert-danger">
            <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
          </div>
          <validation-provider vid="email" rules="required|email" v-slot="{ errors }">
            <div class="card-body">
              <div class="small card-title font-weight-bold">{{ $t('SignContractPage.reviewContract') }}</div>
              <div class="form-group">
                <label for="userEmail" class="sr-only">{{ $t('SignContractPage.email') }}</label>
                <input type="text"
                       id="userEmail"
                       class="form-control form-control-lg"
                       :class="{'is-invalid': errors.length}"
                       inputmode="email"
                       :placeholder="$t('SignContractPage.emailPlaceholder')"
                       v-model="email"
                >
                <span class="invalid-feedback" v-if="errors.length">{{ errors[0] }}</span>
              </div>
            <button id="button-send-contract-to-email" class="btn btn-primary btn-lg btn-block" @click="send()" :disabled="emailSending">
                <template v-if="emailSending">
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                </template>
                <template v-else>
                  {{ $t('SignContractPage.send') }}
                </template>
              </button>
            <template v-if="showDownloadLink">
              <hr class="my-4"/>
              <div id="contract-download-description" class="text-muted text-center my-3">
                {{ $t('SignContractPage.downloadDescription') }}
              </div>
              <button id="button-download-contract" class="btn btn-outline-primary btn-lg btn-block" @click="download">
                {{ $t('SignContractPage.download') }}
              </button>
            </template>
            </div>
          </validation-provider>
        </div>
      </validation-observer>
      <div class="card card-rounded" :class="{'disabled': !contractSent || notAvailable, 'card-shadow': contractSent, 'border-transparent': contractSent}">
        <Alert v-if="notAvailable" type="warning" class="mb-0">
          <div>{{ this.notAvailableError }}{{ this.addNotAvailableReason }}</div>
        </Alert>
        <Alert v-if="authenticationFailed" type="warning" class="mb-0">
          <div>{{ $t('SignContractPage.authenticationFailedError') }}{{ this.addAuthenticationFailedReason }}</div>
        </Alert>
        <div class="card-body">
          <div class="small card-title font-weight-bold" :class="{'text-muted-more': !contractSent}">{{ $t('SignContractPage.signContract') }}</div>
          <button id="sign-contract-button" class="btn btn-primary btn-lg btn-block" :disabled="!contractSent || signing || notAvailable"
                  @click="initializeSigning()">
            <template v-if="signing">
              <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </template>
            <template v-else>
              {{ $t('SignContractPage.sign') }}
            </template>
          </button>
          <div class="mt-2 small text-muted text-center" :class="{'text-muted-more': !contractSent}">
            {{ $t(`SignContractPage.signHint${this.isDesktop ? 'Otp' : 'Pki'}`) }}
          </div>
        </div>
      </div>
      <div class="flex-grow-1"></div>
    </template>
  </div>
</template>

<script>
import TopNav from '@shared/components/TopNav'
import Alert from '@shared/components/Alert'
import store from '@shared/store'
import settings from '@shared/settings'
import LoanApplicationMixin from '@shared/mixins/LoanApplicationMixin'
import ContractSigningMixin from '@shared/mixins/ContractSigningMixin'

export default {
    name: 'SignContractPage',
    components: {
      TopNav, Alert
    },
    mixins: [LoanApplicationMixin, ContractSigningMixin],
    data() {
      return {
        errors: [],
        email: store.state.currentUser.email || '',
        emailSending: false,
        contractSent: false,
        loan: {},
        loading: true,
        showDownloadLink: settings.isDownloadContractsSupported()
      }
    },
    created() {
      this.setReference(this.$route.params.reference).then(() => {
        this.getLoan()
        this.calculateDraft(this.$route.params.reference, this.loan)
      })
    },
    methods: {
      getLoan() {
        const application = store.getPendingApplications().find(application => application.reference === this.reference)
        this.loan = {
          amount: application.loanAmount,
          periodInMonths: application.loanPeriodInMonths
        }
        if (application.productType === 'COMFORT') {
          this.setComfort()
        }
      },
      sendToEmail() {
        this.emailSending = true
        return this.$http.post(`/api/loans/${this.reference}/email/contract`, { email: this.email })
          .then(() => {
            this.$successToast(this.$t('SignContractPage.wasSent.title'), this.$t('SignContractPage.wasSent.message'))
            this.emailSending = false
            this.contractSent = true
          })
          .catch((error) => {
            this.errors = error.response.data.errors
            this.emailSending = false
          })
      },
      download() {
        return this.$http
            .get(`/api/loans/${this.reference}/contract/downloading-hash`)
            .then(response => {
              const hash = response.data.hash
              const params = `hash=${hash}`
              window.location = `${settings.backendUrl()}/api/loans/${this.reference}/unsigned-contract/download?${params}`
              this.contractSent = true
            })
      },
      send() {
        return this.$refs.form.validate().then(this.onValidationDone)
      },
      onValidationDone(isSuccess) {
        if (isSuccess) {
          this.sendToEmail()
        }
      }
    }
  }
</script>
